import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { openLaunchedLessonAsHost } from '../../lib/classroom/openLaunchedLessonAsHost';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Loader2 } from 'lucide-react';
import { FaHandshake, FaSchool, FaCopy, FaChalkboardTeacher, FaPlay, FaLocationArrow, FaUsers, FaUserTimes, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useClassSession } from '../../contexts/ClassSessionContext';
import { useLesson } from '../../contexts/LessonContext';
import { db } from '../../config/firebase';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { removeStudentFromSession } from '../../services/classSessionService';
import { StudentScreen360Preview } from '../../Components/StudentScreen360Preview';
import { ActiveSessionCodeBadge } from '../../Components/classSession/ActiveSessionCodeBadge';
import { Card, CardContent } from '../../Components/ui/card';
import { Button } from '../../Components/ui/button';
import { Badge } from '../../Components/ui/badge';
import type { Partner, PartnerEvent } from '../../types/partner';
import {
  approvePartnerTeacher,
  createPartnerSchool,
  createSchoolInvite,
  fetchPartnerActivity,
  fetchPartnerMe,
  launchPartnerDemoLesson,
  listPartnerSchools,
  recordPartnerHeartbeat,
  recordPartnerLaunchTelemetry,
  startPartnerDemoSession,
} from '../../services/partnerService';
import { listMyLessons, type UserGeneratedLesson } from '../../services/userLessonService';

type SchoolRow = {
  id: string;
  name?: string;
  schoolCode?: string;
  city?: string;
  state?: string;
  approvalStatus?: string;
};

type ClassRow = {
  id: string;
  class_name?: string;
  school_id?: string;
};

type TeacherRow = {
  id: string;
  name?: string;
  displayName?: string;
  email?: string;
  school_id?: string;
  approvalStatus?: string;
};

type DemoLesson = {
  id: string;
  chapterId: string;
  topicId: string;
  title: string;
  sceneId?: string;
  lessonType?: 'curriculum' | 'user_generated';
};

const STREET_VIEW_CLASS_LAUNCH_LIMIT = 50;

const PartnerDashboard = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { startLesson: startLessonInPlayer } = useLesson();
  const {
    activeSession: liveSession,
    activeSessionId,
    progressList,
    bindActiveSession,
    endSession,
    leaveSessionAsTeacher,
    sessionLoading,
  } = useClassSession();
  const [loading, setLoading] = useState(true);
  /*
    True once the first snapshot has landed.

    The page used to render a full-screen spinner whenever `loading` was true,
    and every re-subscribe set it true again — so a write to the user document
    replaced a correct, populated dashboard with a spinner. After the first load
    there is always something worth showing, and live updates land in place.
  */
  const [hasLoaded, setHasLoaded] = useState(false);

  // Latches on the first completed load and never unlatches, so a later refresh
  // can never put the page back behind a spinner.
  useEffect(() => {
    if (!loading) setHasLoaded(true);
  }, [loading]);

  const [partner, setPartner] = useState<Partner | null>(null);
  const [trialActive, setTrialActive] = useState(true);
  const [trialBlockReason, setTrialBlockReason] = useState<string | null>(null);
  const [schools, setSchools] = useState<SchoolRow[]>([]);
  const [events, setEvents] = useState<PartnerEvent[]>([]);
  const [pendingTeachers, setPendingTeachers] = useState<TeacherRow[]>([]);
  const [classesBySchool, setClassesBySchool] = useState<Record<string, ClassRow[]>>({});
  const [selectedClassBySchool, setSelectedClassBySchool] = useState<Record<string, string>>({});
  const [demoLessons, setDemoLessons] = useState<DemoLesson[]>([]);
  const [selectedLessonId, setSelectedLessonId] = useState('');
  const [activeSession, setActiveSession] = useState<{ id: string; code: string; schoolId: string; classId: string } | null>(null);
  const [telemetryConsent, setTelemetryConsent] = useState(false);
  const [removingStudentUid, setRemovingStudentUid] = useState<string | null>(null);
  const [selectedStudentUid, setSelectedStudentUid] = useState<string | null>(null);
  const [studentViewOn, setStudentViewOn] = useState(false);
  const [studentSkyboxUrl, setStudentSkyboxUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [schoolForm, setSchoolForm] = useState({
    name: '',
    city: '',
    state: '',
    contactPerson: '',
    contactPhone: '',
    boardAffiliation: '',
    schoolType: '',
  });

  const schoolIds = useMemo(() => schools.map((s) => s.id), [schools]);

  const daysLeft = useMemo(() => {
    if (!partner?.trial?.endsAt) return null;
    const ms = new Date(partner.trial.endsAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }, [partner?.trial?.endsAt]);

  const refresh = useCallback(async () => {
    if (profile?.role !== 'partner') return;
    setLoading(true);
    try {
      const me = await fetchPartnerMe();
      setPartner(me.partner);
      setTrialActive(me.trialActive);
      setTrialBlockReason(me.trialBlockReason);

      const schoolRes = await listPartnerSchools();
      const schoolList = (schoolRes.schools || []) as SchoolRow[];
      setSchools(schoolList);

      if (me.partner?.id) {
        const activity = await fetchPartnerActivity(me.partner.id);
        setEvents(activity.events || []);
      }

      // Load classes per school
      const classMap: Record<string, ClassRow[]> = {};
      for (const school of schoolList) {
        const classSnap = await getDocs(
          query(collection(db, 'classes'), where('school_id', '==', school.id))
        );
        classMap[school.id] = classSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as ClassRow[];
      }
      setClassesBySchool(classMap);

      const chapterSnap = await getDocs(collection(db, 'curriculum_chapters'));
      const curriculumLessons = chapterSnap.docs.flatMap((chapterDoc) => {
        const chapter = chapterDoc.data();
        const topics = Array.isArray(chapter.topics) ? chapter.topics : [];
        return topics
          .filter((topic: Record<string, unknown>) => topic.isDemo === true && (topic.approval as { approved?: boolean } | undefined)?.approved !== false)
          .map((topic: Record<string, unknown>) => ({
            id: `${chapterDoc.id}:${String(topic.topic_id || topic.id || '')}`,
            chapterId: chapterDoc.id,
            topicId: String(topic.topic_id || topic.id || ''),
            title: String(topic.title || topic.topic_name || chapter.chapter_name || chapter.title || 'Demo lesson'),
            sceneId: typeof topic.scene_id === 'string' ? topic.scene_id : undefined,
            lessonType: 'curriculum' as const,
          }))
          .filter((topic: DemoLesson) => Boolean(topic.topicId));
      });

      // Partner-owned Street View tours are launchable immediately (no Super Admin approval).
      let streetViewLessons: DemoLesson[] = [];
      try {
        const mine = await listMyLessons();
        streetViewLessons = (mine.lessons || [])
          .filter((lesson: UserGeneratedLesson) => {
            const stops = lesson.streetViewTour?.stops;
            return lesson.source === 'street_view' && Array.isArray(stops) && stops.length > 0;
          })
          .map((lesson: UserGeneratedLesson) => {
            const firstStop = lesson.streetViewTour!.stops[0];
            return {
              id: `ug:${lesson.id}`,
              chapterId: lesson.id,
              topicId: `${lesson.id}__stop_${firstStop.id}`,
              title: `${lesson.title || 'Street View tour'} (Street View)`,
              lessonType: 'user_generated' as const,
            };
          });
      } catch (err) {
        console.warn('Could not load Street View tours for partner dashboard', err);
      }

      setDemoLessons([...streetViewLessons, ...curriculumLessons]);

      // Pending teachers in partner schools
      if (schoolList.length > 0) {
        const teacherSnap = await getDocs(
          query(
            collection(db, 'users'),
            where('role', '==', 'teacher'),
            where('approvalStatus', '==', 'pending')
          )
        );
        const ids = new Set(schoolList.map((s) => s.id));
        const teachers = teacherSnap.docs
          .map((d) => ({ id: d.id, ...d.data() } as TeacherRow))
          .filter((t) => t.school_id && ids.has(t.school_id));
        setPendingTeachers(teachers);
      } else {
        setPendingTeachers([]);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Failed to load partner dashboard');
    } finally {
      setLoading(false);
    }
  }, [profile?.role]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (profile?.role !== 'partner') return;
    recordPartnerHeartbeat('dashboard').catch(() => {});
  }, [profile?.role]);

  useEffect(() => {
    const session = activeSession ?? liveSession;
    if (!session?.id || session.status === 'ended') return;
    bindActiveSession(session.id);
  }, [activeSession, liveSession?.id, liveSession?.status, bindActiveSession]);

  const visibleProgress = useMemo(() => {
    const removed = new Set(
      Array.isArray(liveSession?.removed_student_uids) ? liveSession.removed_student_uids : []
    );
    return progressList.filter((s) => s?.student_uid && !removed.has(s.student_uid));
  }, [progressList, liveSession?.removed_student_uids]);

  useEffect(() => {
    if (!selectedStudentUid) return;
    if (!visibleProgress.some((s) => s.student_uid === selectedStudentUid)) {
      setSelectedStudentUid(null);
      setStudentViewOn(false);
    }
  }, [visibleProgress, selectedStudentUid]);

  useEffect(() => {
    const launched = liveSession?.launched_lesson;
    if (!launched || !visibleProgress.length) {
      setStudentSkyboxUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { getLessonBundle } = await import('../../services/firestore/getLessonBundle');
        const bundle = await getLessonBundle({
          chapterId: launched.chapter_id,
          topicId: launched.topic_id,
          lang: 'en',
          source: launched.lesson_type === 'user_generated' ? 'user_generated' : 'curriculum',
        });
        const topic = bundle.chapter?.topics?.find((item: any) => item.topic_id === launched.topic_id) || bundle.chapter?.topics?.[0];
        const url = (bundle.skybox as any)?.imageUrl || (bundle.skybox as any)?.file_url || topic?.skybox_url || null;
        if (!cancelled) setStudentSkyboxUrl(url);
      } catch {
        if (!cancelled) setStudentSkyboxUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [liveSession?.launched_lesson, visibleProgress.length]);

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Could not copy');
    }
  };

  const handleCreateSchool = async () => {
    if (!schoolForm.name.trim()) {
      toast.error('School name is required');
      return;
    }
    setBusy(true);
    try {
      const result = await createPartnerSchool(schoolForm);
      toast.success(`School created. Code: ${result.schoolCode}`);
      setSchoolForm({
        name: '',
        city: '',
        state: '',
        contactPerson: '',
        contactPhone: '',
        boardAffiliation: '',
        schoolType: '',
      });
      await refresh();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create school');
    } finally {
      setBusy(false);
    }
  };

  const handleInvite = async (schoolId: string) => {
    setBusy(true);
    try {
      const result = await createSchoolInvite(schoolId);
      await copyText(result.inviteUrl, 'Invite link');
      toast.info('Share this invite so the school admin can claim access (no shared password).');
      await refresh();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create invite');
    } finally {
      setBusy(false);
    }
  };

  const handleStartDemo = async (schoolId: string) => {
    const classId = selectedClassBySchool[schoolId];
    if (!classId) {
      toast.error('Select a class first (create classes under Class Management if needed)');
      return;
    }
    if (!trialActive) {
      toast.error(trialBlockReason || 'Trial inactive');
      return;
    }
    setBusy(true);
    try {
      const result = await startPartnerDemoSession(schoolId, classId);
      toast.success(`Demo session ready. Code: ${result.sessionCode}`);
      await copyText(result.sessionCode, 'Session code');
      setActiveSession({ id: result.sessionId, code: result.sessionCode, schoolId, classId });
      bindActiveSession(result.sessionId);
      sessionStorage.setItem(
        'learnxr_partner_demo_session',
        JSON.stringify({ id: result.sessionId, code: result.sessionCode, schoolId, classId })
      );
      if (telemetryConsent && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async ({ coords }) => {
            try {
              const location = await recordPartnerLaunchTelemetry(result.sessionId, {
                latitude: coords.latitude,
                longitude: coords.longitude,
              });
              toast.info(`Launch telemetry recorded${location.city ? ` for ${location.city}` : ''}.`);
            } catch {
              toast.info('Demo started. Location telemetry could not be recorded.');
            }
          },
          () => toast.info('Demo started without location telemetry.'),
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );
      }
      await refresh();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to start demo');
    } finally {
      setBusy(false);
    }
  };

  const handleLaunchLesson = async () => {
    const lesson = demoLessons.find((item) => item.id === selectedLessonId);
    if (!activeSession || !lesson) {
      toast.error('Start a demo session and select a Street View tour or lesson first.');
      return;
    }
    setBusy(true);
    try {
      const result = await launchPartnerDemoLesson(activeSession.id, {
        chapterId: lesson.chapterId,
        topicId: lesson.topicId,
        sceneId: lesson.sceneId,
        title: lesson.title,
        lessonType: lesson.lessonType || 'curriculum',
      });
      if (lesson.lessonType === 'user_generated') {
        toast.success(
          `Street View tour launched. ${(result as { classLaunchesRemaining?: number }).classLaunchesRemaining ?? partner?.trial?.classLaunchesRemaining ?? 0} class launches remaining.`
        );
      } else {
        toast.success(`Lesson launched. ${result.lessonLaunchesRemaining} demo lesson launches remaining.`);
      }
      await refresh();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to launch lesson');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveStudent = async (studentUid: string) => {
    if (!activeSessionId || !profile?.uid) return;
    setRemovingStudentUid(studentUid);
    try {
      const ok = await removeStudentFromSession(activeSessionId, profile.uid, studentUid);
      if (ok) toast.success('Student removed from the demo session.');
      else toast.error('Could not remove student.');
    } finally {
      setRemovingStudentUid(null);
    }
  };

  const handleEndDemo = async () => {
    const ok = await endSession();
    if (ok) {
      setActiveSession(null);
      sessionStorage.removeItem('learnxr_partner_demo_session');
      toast.success('Demo session ended.');
    } else {
      toast.error('Could not end the demo session.');
    }
  };

  const handleControlClassView = async () => {
    const launched = liveSession?.launched_lesson;
    if (!launched || !activeSessionId) {
      toast.error('Launch a demo lesson before opening class view controls.');
      return;
    }
    setBusy(true);
    try {
      const result = await openLaunchedLessonAsHost({
        launched,
        sessionId: activeSessionId,
        navigate,
        // Partners start LessonContext and stash the session id; the teacher
        // path does neither, so it stays here rather than in the shared opener.
        onLessonReady: (payload) => {
          startLessonInPlayer(payload.chapter as any, payload.topic as any);
          try {
            sessionStorage.setItem('learnxr_class_session_id', activeSessionId);
          } catch (storageError) {
            console.warn('Could not stash the class session id:', storageError);
          }
        },
      });
      if (!result.ok) {
        throw new Error(
          result.reason === 'tour-missing'
            ? '360° tour not found.'
            : 'The launched lesson is no longer available.'
        );
      }
    } catch (error: any) {
      toast.error(error?.message || 'Could not open the class view controls.');
    } finally {
      setBusy(false);
    }
  };

  const handleTeacherDecision = async (teacherUid: string, approve: boolean) => {
    setBusy(true);
    try {
      await approvePartnerTeacher(teacherUid, approve);
      toast.success(approve ? 'Teacher approved' : 'Teacher rejected');
      await refresh();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update teacher');
    } finally {
      setBusy(false);
    }
  };

  // Only before anything has ever been shown. A refresh over existing
  // content must not blank the page.
  if (loading && !hasLoaded) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="p-8 text-muted-foreground">
        Partner profile not found. Ask a superadmin to approve your application.
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-7 bg-gradient-to-b from-background via-background to-teal-950/[0.03] p-4 pt-6 md:p-8">
      <ActiveSessionCodeBadge role="partner" />
      <section className="relative overflow-hidden rounded-2xl border border-teal-400/20 bg-card/75 p-5 shadow-sm backdrop-blur-xl sm:p-7">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-teal-400/10 blur-3xl" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-teal-400/25 bg-teal-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-teal-400">
              <FaHandshake className="h-3 w-3" /> Channel Partner Workspace
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">{partner.organizationName}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Plan live demonstrations, guide classroom experiences, and monitor partner activity from one secure workspace.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={trialActive ? 'default' : 'destructive'} className="rounded-full px-3 py-1 capitalize">
              {partner.status}
            </Badge>
            <div className="rounded-xl border border-border bg-background/70 px-3 py-2 text-right">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Trial remaining</p>
              <p className="text-sm font-semibold">{daysLeft ?? '—'} days</p>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-3">
        <Card className="border-border/80 bg-card/80 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Street View class launches</p>
            <p className="mt-2 text-2xl font-bold tabular-nums">{partner.trial?.classLaunchesRemaining ?? 0}<span className="text-base font-medium text-muted-foreground"> / {partner.trial?.classLaunchesLimit ?? STREET_VIEW_CLASS_LAUNCH_LIMIT}</span></p>
            <p className="mt-1 text-xs text-muted-foreground">1 live demo session = 1 credit (no double-count on tour launch)</p>
          </CardContent>
        </Card>
        <Card className="border-border/80 bg-card/80 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Curriculum demo lessons</p>
            <p className="mt-2 text-2xl font-bold tabular-nums">{partner.trial?.lessonLaunchesRemaining ?? 0}<span className="text-base font-medium text-muted-foreground"> / {partner.trial?.lessonLaunchesLimit ?? 200}</span></p>
            <p className="mt-1 text-xs text-muted-foreground">Approved curriculum demos only</p>
          </CardContent>
        </Card>
        <Card className={!trialActive ? 'border-destructive/50 bg-destructive/5' : 'border-teal-400/20 bg-teal-400/[0.04]'}>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Demo environment</p>
            <p className="mt-2 text-sm font-semibold">{partner.demoSchoolId ? 'In3D.ai demo class ready' : 'Setup required'}</p>
            <p className="mt-1 text-xs text-muted-foreground">{trialBlockReason || 'Isolated workspace with consent-based launch telemetry'}</p>
          </CardContent>
        </Card>
      </section>

      <Card className="border-teal-400/30 bg-card/85 shadow-sm">
        <CardContent className="space-y-5 p-5 sm:p-6">
          <div className="flex items-center gap-3 font-medium">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-400/10 text-teal-400"><FaPlay className="h-4 w-4" /></span>
            <div><p>Live demo classroom</p><p className="text-xs font-normal text-muted-foreground">Start, launch, and guide a partner session</p></div>
          </div>
          {!activeSession ? (
            <p className="text-sm text-muted-foreground">
              Choose your isolated demo class below, then start a session to receive a student join code.
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-3 rounded-xl border border-teal-400/20 bg-teal-400/5 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium">Session code</p>
                  <code className="mt-1 inline-block rounded bg-background px-2 py-1 font-mono text-lg tracking-widest">
                    {activeSession.code}
                  </code>
                </div>
                <Button size="sm" variant="outline" onClick={() => copyText(activeSession.code, 'Session code')}>
                  <FaCopy className="mr-2 h-3 w-3" /> Copy code
                </Button>
              </div>
              <div className="grid gap-3 rounded-xl border border-border bg-background/40 p-3 sm:grid-cols-[1fr_auto_auto]">
                <select
                  value={selectedLessonId}
                  onChange={(event) => setSelectedLessonId(event.target.value)}
                  className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select a Street View tour or lesson</option>
                  {demoLessons.map((lesson) => (
                    <option key={lesson.id} value={lesson.id}>{lesson.title}</option>
                  ))}
                </select>
                <Button variant="outline" disabled={busy} onClick={() => navigate('/create/street-view')}>
                  Create Street View
                </Button>
                <Button disabled={busy || !selectedLessonId} onClick={handleLaunchLesson}>
                  <FaPlay className="mr-2 h-3 w-3" /> Launch
                </Button>
              </div>
              {demoLessons.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No Street View tours yet. Create one to launch immediately — Super Admin approval is not required.
                </p>
              )}
            </>
          )}
          <label className="flex items-start gap-3 rounded-xl border border-border bg-muted/20 p-3 text-sm">
            <input
              type="checkbox"
              checked={telemetryConsent}
              onChange={(event) => setTelemetryConsent(event.target.checked)}
              className="mt-1"
            />
            <span>
              <span className="flex items-center gap-2 font-medium"><FaLocationArrow className="text-teal-400" /> Share coarse launch location</span>
              <span className="mt-1 block text-xs text-muted-foreground">
                With consent, In3D.ai records city and country for this demo launch. Precise coordinates are discarded and launch telemetry is retained for 90 days.
              </span>
            </span>
          </label>
        </CardContent>
      </Card>

      {liveSession && liveSession.status !== 'ended' && (
        <Card className="border-primary/30 bg-card/85 shadow-sm">
          <CardContent className="space-y-5 p-5 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center gap-2 font-medium"><FaUsers className="text-primary" /> Live class controls</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Session {liveSession.session_code} · {liveSession.status} · {visibleProgress.length} connected student{visibleProgress.length === 1 ? '' : 's'}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => copyText(liveSession.session_code, 'Session code')}><FaCopy className="mr-2 h-3 w-3" /> Copy code</Button>
                {liveSession.launched_lesson && (
                  <Button size="sm" variant="outline" disabled={busy} onClick={handleControlClassView}>
                    <FaEye className="mr-2 h-3 w-3" /> Control class view
                  </Button>
                )}
                <Button size="sm" variant="destructive" disabled={sessionLoading} onClick={handleEndDemo}>End session</Button>
              </div>
            </div>
            {!liveSession.launched_lesson ? (
              <p className="rounded-lg border border-border bg-muted/20 p-3 text-sm text-muted-foreground">
                Launch a Street View tour or curriculum lesson above, or open Street View Tour Creator to start live instruction.
              </p>
            ) : (
              <p className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-primary">
                Live lesson: {liveSession.launched_lesson.title || liveSession.launched_lesson.topic_id}. Open the launched lesson in your player to broadcast your 360° view.
              </p>
            )}
            {visibleProgress.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No students have joined yet. Share the session code with guests or students.</p>
            ) : (
              <div className="grid gap-3 lg:grid-cols-[1fr_1.3fr]">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Student roster and progress</p>
                  {visibleProgress.map((student) => (
                    <div key={student.student_uid} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background/30 p-3 transition hover:border-primary/30">
                      <button className="min-w-0 text-left" onClick={() => setSelectedStudentUid(student.student_uid)}>
                        <p className="truncate text-sm font-medium">{student.display_name || student.email || `Student ${student.student_uid.slice(0, 6)}`}</p>
                        <p className="text-xs capitalize text-muted-foreground">{student.phase || 'connected'}</p>
                      </button>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedStudentUid(student.student_uid);
                            setStudentViewOn((on) =>
                              selectedStudentUid === student.student_uid && on ? false : true
                            );
                          }}
                          title="See this student's view"
                        >
                          <FaEye className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="destructive" disabled={removingStudentUid === student.student_uid} onClick={() => handleRemoveStudent(student.student_uid)} title="Remove student">
                          {removingStudentUid === student.student_uid ? <Loader2 className="h-3 w-3 animate-spin" /> : <FaUserTimes className="h-3 w-3" />}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">Student view</p>
                    <Button
                      size="sm"
                      variant={studentViewOn ? 'default' : 'outline'}
                      onClick={() => setStudentViewOn((v) => !v)}
                    >
                      {studentViewOn ? 'On' : 'Off'}
                    </Button>
                  </div>
                  {(() => {
                    if (!studentViewOn) {
                      return (
                        <div className="flex aspect-video items-center justify-center rounded-xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                          Student view is off. Select a student and turn it on to inspect their live 360° view.
                        </div>
                      );
                    }
                    const student = visibleProgress.find((item) => item.student_uid === selectedStudentUid) || null;
                    const name = student?.display_name || student?.email || 'Student';
                    return student?.student_view && studentSkyboxUrl ? (
                      <StudentScreen360Preview
                        skyboxUrl={studentSkyboxUrl}
                        view={student.student_view}
                        studentName={name}
                        phaseLabel={student.phase || 'Connected'}
                        getApiBaseUrl={getApiBaseUrl}
                      />
                    ) : (
                      <div className="flex aspect-video items-center justify-center rounded-xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                        {student ? `${name}'s 360° view appears once they enter the live lesson.` : 'Select a student to inspect their live view.'}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-border/80 bg-card/85 shadow-sm">
        <CardContent className="space-y-5 p-5 sm:p-6">
          <div className="flex items-center gap-3 font-medium">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary"><FaSchool className="h-4 w-4" /></span>
            <div><p>Add a school</p><p className="text-xs font-normal text-muted-foreground">Create a portfolio location for future demonstrations</p></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {(
              [
                ['name', 'School name *'],
                ['city', 'City'],
                ['state', 'State'],
                ['contactPerson', 'Contact person'],
                ['contactPhone', 'Contact phone'],
                ['boardAffiliation', 'Board'],
                ['schoolType', 'School type'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="text-sm space-y-1">
                <span className="text-muted-foreground">{label}</span>
                <input
                  className="w-full rounded-lg border border-border bg-background/70 px-3 py-2 transition focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/15"
                  value={schoolForm[key]}
                  onChange={(e) => setSchoolForm((f) => ({ ...f, [key]: e.target.value }))}
                />
              </label>
            ))}
          </div>
          <Button disabled={busy || partner.status === 'suspended'} onClick={handleCreateSchool}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create school'}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-border/80 bg-card/85 shadow-sm">
        <CardContent className="space-y-5 p-5 sm:p-6">
          <div className="flex items-center justify-between"><div><p className="font-medium">Demo locations</p><p className="mt-1 text-xs text-muted-foreground">Choose a class and begin a controlled live session</p></div><Badge variant="outline">{schools.length}</Badge></div>
          {schools.length === 0 ? (
            <p className="text-sm text-muted-foreground">No schools yet. Create one above for your demo visit.</p>
          ) : (
            <div className="space-y-4">
              {schools.map((school) => {
                const classes = classesBySchool[school.id] || [];
                return (
                  <div key={school.id} className="space-y-4 rounded-xl border border-border bg-background/25 p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <div className="font-semibold">{school.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {[school.city, school.state].filter(Boolean).join(', ')}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="rounded-md bg-muted px-2 py-1 font-mono text-sm">
                          {school.schoolCode || '—'}
                        </code>
                        {school.schoolCode && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyText(school.schoolCode!, 'School code')}
                          >
                            <FaCopy className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Share the school code so teachers/students can join during onboarding.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                      <select
                        className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                        value={selectedClassBySchool[school.id] || ''}
                        onChange={(e) =>
                          setSelectedClassBySchool((m) => ({ ...m, [school.id]: e.target.value }))
                        }
                      >
                        <option value="">Select class for demo</option>
                        {classes.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.class_name || c.id}
                          </option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        disabled={busy || !trialActive || partner.status === 'suspended'}
                        onClick={() => handleStartDemo(school.id)}
                      >
                        Start demo session
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => handleInvite(school.id)}
                      >
                        Copy school-admin invite
                      </Button>
                    </div>
                    {classes.length === 0 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        No classes yet. Create classes via Class Management (or have the school admin do it)
                        before starting a session.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80 bg-card/85 shadow-sm">
        <CardContent className="space-y-5 p-5 sm:p-6">
          <div className="flex items-center gap-3 font-medium">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary"><FaChalkboardTeacher className="h-4 w-4" /></span>
            <div><p>Teacher approvals</p><p className="text-xs font-normal text-muted-foreground">Review educators assigned to your portfolio</p></div>
          </div>
          {pendingTeachers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pending teachers in your schools.</p>
          ) : (
            <ul className="space-y-3">
              {pendingTeachers.map((t) => (
                <li
                  key={t.id}
                  className="flex flex-col gap-2 rounded-xl border border-border bg-background/25 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="text-sm">
                    <div className="font-medium">{t.displayName || t.name || t.email || t.id}</div>
                    <div className="text-muted-foreground">{t.email}</div>
                    <div className="text-xs text-muted-foreground">
                      School:{' '}
                      {schools.find((s) => s.id === t.school_id)?.name || t.school_id}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" disabled={busy} onClick={() => handleTeacherDecision(t.id, true)}>
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busy}
                      onClick={() => handleTeacherDecision(t.id, false)}
                    >
                      Reject
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80 bg-card/85 shadow-sm">
        <CardContent className="space-y-4 p-5 sm:p-6">
          <div><p className="font-medium">Activity timeline</p><p className="mt-1 text-xs text-muted-foreground">Recent demo, school, and teacher events</p></div>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No activity yet.</p>
          ) : (
            <ul className="text-sm space-y-2 max-h-64 overflow-y-auto">
              {events.map((e) => (
                <li key={e.id} className="flex justify-between gap-4 rounded-lg border border-border/60 bg-background/20 px-3 py-2">
                  <span className="font-medium capitalize">{e.type.replaceAll('_', ' ')}</span>
                  <span className="text-muted-foreground text-xs truncate">
                    {e.schoolId
                      ? schools.find((s) => s.id === e.schoolId)?.name || e.schoolId
                      : '—'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {schoolIds.length > 0 && (
            <p className="text-xs text-muted-foreground pt-2">
              Tracking {schoolIds.length} school(s) under your partner_id.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerDashboard;
